import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  ApplicationRef,
  Inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { StorageService } from '@univacity/angular/datas';
import { BehaviorSubject } from 'rxjs';
import { Setting } from '@univacity/shared/types';
import { Theme } from '../interfaces/theme';

@Injectable({
  providedIn: 'root',
})
export class ColorSchemeService {
  private renderer: Renderer2;
  private colorScheme: Theme;
  private themes: Theme[] = ['light', 'dark', 'auto'];
  theme$ = new BehaviorSubject<'light' | 'dark'>('light'); // <- initial theme
  private activeTheme: 'light' | 'dark';

  // Define prefix for clearer and more readable class names in scss files
  private colorSchemePrefix = 'color-scheme-';

  constructor(
    rendererFactory: RendererFactory2,
    private ref: ApplicationRef,
    private storage: StorageService,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    // Create new renderer from renderFactory, to make it possible to use renderer2 in a service
    this.renderer = rendererFactory.createRenderer(null, null);
    this.watchThemeDynamicChange();
  }
  private setActive(theme: 'light' | 'dark') {
    this.activeTheme = theme;
    this.theme$.next(theme);
  }

  private detectPrefersColorScheme() {
    // Detect if prefers-color-scheme is supported
    if (
      !isPlatformServer(this.platformId) &&
      window.matchMedia('(prefers-color-scheme)').media !== 'not all'
    ) {
      // Set colorScheme to Dark if prefers-color-scheme is dark. Otherwise, set it to Light.
      this.activeTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';
    } else {
      // If the browser does not support prefers-color-scheme, set the default to light.
      this.setActive('light');
    }
  }
  private watchThemeDynamicChange() {
    if (isPlatformServer(this.platformId)) return;
    // Watch for changes of the preference
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        const theme = e.matches ? 'dark' : 'light';
        if (this.colorScheme === 'auto' && this.activeTheme !== theme) {
          this.updateClass(theme);
        }
      });
  }

  private updateClass(scheme: Theme) {
    if (scheme === 'auto') return;
    // Remove the old color-scheme class
    this.themes.forEach((t) =>
      this.renderer.removeClass(this.doc.body, this.colorSchemePrefix + t)
    );

    // Add the new / current color-scheme class
    this.renderer.addClass(this.doc.body, this.colorSchemePrefix + scheme);
    this.setActive(scheme);
    // Trigger refresh of UI
    this.ref.tick();
  }

  init() {
    this.setColorScheme();
    this.renderer.addClass(
      this.doc.body,
      this.colorSchemePrefix + this.activeTheme
    );
  }

  update(scheme: Theme) {
    this.colorScheme = scheme;
    if (scheme === 'auto') {
      this.detectPrefersColorScheme();
    } else this.setActive(scheme);

    // Save prefers-color-scheme to localStorage
    this.storage.setItem(Setting.prefersColor, scheme);

    this.updateClass(this.activeTheme);
  }
  private setColorScheme() {
    const localStorageColorScheme =
      (this.storage.getItem(Setting.prefersColor) as Theme | null) || 'auto';
    // Check if any prefers-color-scheme is stored in localStorage
    if (localStorageColorScheme) {
      // Save prefers-color-scheme from localStorage
      this.colorScheme = localStorageColorScheme;
      if (this.colorScheme !== 'auto') this.setActive(this.colorScheme);
    }
    if (!localStorageColorScheme || this.colorScheme === 'auto') {
      // If no prefers-color-scheme is stored in localStorage, try to detect OS default prefers-color-scheme
      this.colorScheme = 'auto';
      this.storage.setItem(Setting.prefersColor, 'auto');
      this.detectPrefersColorScheme();
    }
  }

  currentActive() {
    return this.colorScheme;
  }
}
